import { Card } from "@/components/ui/card";
import { Lightbulb, Heart, Brain, Target } from "lucide-react";

interface PregnancyInsightsProps {
  babyId: string;
}

export function PregnancyInsights({ babyId }: PregnancyInsightsProps) {
  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-r from-primary/10 to-secondary/10">
        <h3 className="text-2xl font-bold mb-2">Your Journey Insights</h3>
        <p className="text-muted-foreground">
          Understanding patterns and development to support you and your baby
        </p>
      </Card>

      <div className="grid gap-6">
        <Card className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Brain className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold mb-2">Developmental Milestones</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Every baby develops at their own pace. Typical milestones provide a general guide, but the 
                range of "normal" is wide. If you have concerns, your healthcare provider can assess whether 
                development is on track.
              </p>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• <strong>0-3 months:</strong> Lifts head, tracks objects, social smiles</li>
                <li>• <strong>4-6 months:</strong> Rolls over, reaches for toys, babbles</li>
                <li>• <strong>7-9 months:</strong> Sits independently, crawls, responds to name</li>
                <li>• <strong>10-12 months:</strong> Pulls to stand, first words, pincer grasp</li>
                <li>• <strong>12-18 months:</strong> First steps, follows simple commands</li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Heart className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold mb-2">Sleep & Feeding Patterns</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Understanding your baby's rhythms helps establish routines that work for your family. Patterns 
                will emerge and change as your baby grows.
              </p>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• <strong>Newborns (0-3 months):</strong> Feed 8-12x/day, sleep 14-17 hours in short bursts</li>
                <li>• <strong>Infants (4-11 months):</strong> Feed 4-6x/day, sleep 12-15 hours with naps</li>
                <li>• <strong>Toddlers (1-2 years):</strong> 3 meals + snacks, sleep 11-14 hours with 1-2 naps</li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Lightbulb className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold mb-2">Tracking Benefits</h3>
              <p className="text-sm text-muted-foreground mb-3">
                The data you're collecting serves multiple purposes beyond just record-keeping:
              </p>
              <ul className="text-sm space-y-2 text-muted-foreground">
                <li>
                  <strong>Pattern Recognition:</strong> See trends in sleep, feeding, and behaviour to anticipate 
                  needs and optimise routines
                </li>
                <li>
                  <strong>Healthcare Support:</strong> Share concrete data with pediatricians for better 
                  assessments and guidance
                </li>
                <li>
                  <strong>Memory Preservation:</strong> Create a timeline you'll treasure as your child grows
                </li>
                <li>
                  <strong>Confidence Building:</strong> See progress over time, celebrating small wins and 
                  understanding what works
                </li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Target className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold mb-2">Your Postpartum Journey</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Taking care of yourself is just as important as caring for your baby. Your recovery and 
                wellbeing matter.
              </p>
              <ul className="text-sm space-y-2 text-muted-foreground">
                <li>
                  <strong>Physical Recovery:</strong> Give yourself 6-8 weeks minimum to heal. Every body is 
                  different.
                </li>
                <li>
                  <strong>Emotional Wellbeing:</strong> Baby blues are common in the first 2 weeks. If feelings 
                  persist or worsen, reach out for support.
                </li>
                <li>
                  <strong>Rest Priority:</strong> Sleep when baby sleeps isn't just advice-it's essential for healing.
                </li>
                <li>
                  <strong>Support Network:</strong> Accept help, ask for what you need, and connect with other parents.
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
